 
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Positioning: vertically centred, capped at 90 dvh so it never exceeds the
        // viewport on small screens or when content is tall.
        "fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%]",
        // Layout: flex-column so the header/body/footer stack and the body can scroll.
        // overflow-hidden enforces the max-h-[90dvh] cap: a flex child (DialogBody)
        // still needs its own min-h-0 + overflow-y-auto to actually scroll instead of
        // pushing DialogFooter below the fold (LIVE-24, 2026-09-24) — see DialogBody.
        "flex max-h-[90dvh] flex-col overflow-hidden",
        // Visual chrome.
        "border bg-white shadow-lg sm:rounded-lg",
        // Entrance/exit: a plain fade. No zoom or slide, so the panel never drifts toward a corner as it closes.
        "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // flex-none: header never shrinks when the dialog is height-constrained.
      "flex-none flex flex-col space-y-1.5 px-6 pt-6 pb-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/**
 * Scrollable body region for a DialogContent whose content length is
 * content-driven (a variable number of fields/rows/settings) rather than
 * fixed. DialogContent caps at max-h-[90dvh] with overflow-hidden, and
 * DialogHeader/DialogFooter are flex-none, but a flex child's default
 * min-height is `auto` — without an explicit min-h-0 override, tall content
 * inside it refuses to shrink and overflow-y-auto never actually kicks in,
 * so it just pushes DialogFooter below the fold instead of scrolling
 * (reproduced live: LIVE-24, 2026-09-24 — EditFlagDialog's Apply/Cancel
 * became unreachable with 4 settings rendered, no scroll, no way to click
 * them). Any DialogContent whose body can grow past the viewport should
 * wrap that body in DialogBody instead of a plain div, so header and
 * footer stay fixed and only this region scrolls — see dialog.test.tsx's
 * structure suite, which every consumer inherits.
 */
const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-2", className)}
    {...props}
  />
))
DialogBody.displayName = "DialogBody"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // flex-none: footer never shrinks or scrolls — it is always visible.
      // border-t provides a clear visual separator from the scrollable body above.
      "flex-none flex flex-col-reverse gap-2 border-t border-gray-100 px-6 py-4 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
